/**
 * useTableAssist — chamados de mesa do lado do cliente (Hub da Mesa).
 *
 * Envia sempre restaurant_id + table_id (UUID) + session_id; o servidor
 * valida e deriva o número da mesa. Nunca envia o número visível.
 * Toques repetidos devolvem o mesmo chamado — o backend é idempotente e
 * o botão fica bloqueado enquanto houver chamado pendente ou aceite.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { isAssistLocked } from "@/utils/tableAssistState";

const POLL_MS = 5000;
const EMPTY = { waiter: null, bill: null };

export function useTableAssist({ API, restaurantId, tableId, sessionId, source = "qr", enabled = true }) {
  const [requests, setRequests] = useState(EMPTY);
  const [sending, setSending] = useState({ waiter: false, bill: false });
  const [error, setError] = useState(null);
  const clientIdsRef = useRef({});
  const ready = !!(enabled && API && restaurantId && tableId);

  const refresh = useCallback(async () => {
    if (!ready) return;
    try {
      const res = await axios.get(`${API}/public/table-assist/status`, {
        params: { restaurant_id: restaurantId, table_id: tableId, session_id: sessionId || undefined },
      });
      setRequests(res.data?.requests || EMPTY);
    } catch {
      // Falha de rede não pode apagar o estado já mostrado ao cliente.
    }
  }, [ready, API, restaurantId, tableId, sessionId]);

  useEffect(() => {
    if (!ready) return undefined;
    refresh();
    const timer = setInterval(refresh, POLL_MS);
    return () => clearInterval(timer);
  }, [ready, refresh]);

  const callAssist = useCallback(async (type) => {
    if (!ready || isAssistLocked(requests[type]) || sending[type]) return;
    if (!clientIdsRef.current[type]) {
      clientIdsRef.current[type] = `${tableId}-${sessionId || "-"}-${type}-${Date.now()}`;
    }
    setSending((prev) => ({ ...prev, [type]: true }));
    setError(null);
    try {
      const res = await axios.post(`${API}/public/table-assist/request`, {
        restaurant_id: restaurantId,
        table_id: tableId,
        session_id: sessionId || null,
        type,
        source,
        client_request_id: clientIdsRef.current[type],
      });
      const request = res.data?.request;
      if (request) setRequests((prev) => ({ ...prev, [type]: request }));
    } catch (err) {
      setError(err?.response?.data?.detail || "No se pudo avisar al equipo. Inténtalo de nuevo.");
    }
    setSending((prev) => ({ ...prev, [type]: false }));
  }, [ready, API, restaurantId, tableId, sessionId, source, requests, sending]);

  return { requests, sending, error, callAssist, refresh };
}
